import { Box, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';

const gridSize = 12;

interface ScaleGridBoxProps {
	scaleFactor: number;
	zoomScale?: number;
}

const ScaleGridBox: React.FC<ScaleGridBoxProps> = ({
	scaleFactor,
	zoomScale = 1,
}) => {
	// Track viewport size dynamically
	const [viewportSize, setViewportSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		const handleResize = () => {
			setViewportSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const gridSpacing = scaleFactor * gridSize;

	// Use a large canvas size to ensure grid covers everything
	// Add buffer to account for scrolling and dragging beyond viewport
	const gridWidth = Math.max(viewportSize.width * 3, 3000);
	const gridHeight = Math.max(viewportSize.height * 3, 3000);

	// Calculate number of lines to cover the entire grid area
	const numOfVerticalLines = Math.ceil(gridWidth / gridSpacing);
	const numOfHorizontalLines = Math.ceil(gridHeight / gridSpacing);

	console.log('[ScaleGridBox] Grid configuration:', {
		scaleFactor,
		zoomScale,
		gridSize,
		gridSpacing,
		gridLine6Y: 6 * gridSpacing,
		gridLine10X: 10 * gridSpacing,
		viewportWidth: viewportSize.width,
		viewportHeight: viewportSize.height,
		gridWidth,
		gridHeight,
	});

	const verticalLines: object[] = [];

	for (let i = 0; i < numOfVerticalLines; i++) {
		const leftPosition = i * gridSpacing;
		verticalLines.push({
			width: '1px',
			height: '100%',
			position: 'absolute',
			top: '0px',
			left: `${leftPosition}px`,
			backgroundColor: 'rgba(0, 0, 0, 0.1)',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'start',
		});
	}

	const horizontalLines: object[] = [];

	for (let i = 0; i < numOfHorizontalLines; i++) {
		const topPosition = i * gridSpacing;
		horizontalLines.push({
			width: '100%',
			height: '1px',
			position: 'absolute',
			top: `${topPosition}px`,
			left: '0px',
			backgroundColor: 'rgba(0, 0, 0, 0.1)',
			display: 'flex',
			justifyContent: 'start',
			alignItems: 'center',
		});
	}

	// Calculate dynamic font size based on zoom (5px min, 10px max)
	const fontSize = Math.max(5, Math.min(10, 7 * (zoomScale || 1)));
	const showLabels = (zoomScale || 1) >= 0.5;

	return (
		<Box
			sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				width: `${gridWidth}px`,
				height: `${gridHeight}px`,
				zIndex: 0,
				pointerEvents: 'none',
				transform: `scale(${zoomScale || 1})`,
				transformOrigin: 'top left',
			}}
		>
			{verticalLines.map((line, index) => (
				<Box key={index} sx={line}>
					{index > 0 && showLabels && (
						<Typography
							sx={{
								fontSize: `${fontSize}px`,
								color: 'rgba(0, 0, 0, 0.65)',
							}}
						>
							{index}
						</Typography>
					)}
				</Box>
			))}

			{horizontalLines.map((line, index) => (
				<Box key={index} sx={line}>
					{index > 0 && showLabels && (
						<Typography
							sx={{
								fontSize: `${fontSize}px`,
								color: 'rgba(0, 0, 0, 0.65)',
							}}
						>
							{index}
						</Typography>
					)}
				</Box>
			))}
		</Box>
	);
};

export default ScaleGridBox;
